import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HoverScale } from '../animations/MotionComponents';
import clsx from 'clsx';

export const ModernCard = ({ 
  children, 
  className = '', 
  variant = 'default',
  hover = true,
  glow = false 
}) => {
  const variants = {
    default: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
    gradient: 'bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 border border-blue-200 dark:border-purple-900',
    glass: 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg border border-white/20 dark:border-gray-700/20',
  };

  const cardClasses = clsx(
    'rounded-2xl shadow-lg overflow-hidden transition-all duration-300',
    variants[variant],
    glow && 'shadow-glow-lg',
    className
  );

  const CardWrapper = hover ? HoverScale : 'div';

  return (
    <CardWrapper className={cardClasses}>
      {children}
    </CardWrapper>
  );
};

export const CardHeader = ({ children, className = '' }) => (
  <div className={clsx('p-6 border-b border-gray-100 dark:border-gray-700', className)}>
    {children}
  </div>
);

export const CardBody = ({ children, className = '' }) => (
  <div className={clsx('p-6', className)}>
    {children}
  </div>
);

export const CardFooter = ({ children, className = '' }) => (
  <div className={clsx('p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50', className)}>
    {children}
  </div>
);

export const FeatureCard = ({ 
  icon, 
  title, 
  description, 
  image,
  gradient = false,
  className = '' 
}) => {
  return (
    <ModernCard 
      variant={gradient ? 'gradient' : 'default'} 
      className={clsx('h-full', className)}
    >
      {image && (
        <div className="relative h-48 overflow-hidden">
          <motion.img
            src={image}
            alt={title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.3 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </div>
      )}
      <CardBody className="flex flex-col gap-4">
        {icon && (
          <div className="w-14 h-14 rounded-xl bg-gradient-primary flex items-center justify-center text-white text-2xl shadow-glow">
            {icon}
          </div>
        )}
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
          {description}
        </p>
      </CardBody>
    </ModernCard>
  );
};

export const ServiceCard = ({ 
  icon, 
  title, 
  description, 
  features = [],
  image,
  className = '' 
}) => {
  const [imageError, setImageError] = useState(false);
  
  // Define fallback images with proper URLs
  const fallbackImages = {
    'Web Development': 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600&h=400&fit=crop',
    'Mobile App Development': 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600&h=400&fit=crop',
    'Cloud Solutions': 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop',
    'Digital Marketing': 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop'
  };

  const displayImage = imageError ? fallbackImages[title] : (image || fallbackImages[title]);

  return (
    <ModernCard variant="glass" className={clsx('h-full group', className)}>
      {displayImage && (
        <div className="relative h-56 overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800">
          <motion.img
            src={displayImage}
            alt={title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.5 }}
            onError={() => setImageError(true)}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute top-4 left-4 w-16 h-16 rounded-2xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm flex items-center justify-center text-3xl shadow-xl">
            {icon}
          </div>
        </div>
      )}
      <CardBody className="space-y-4">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
          {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
          {description}
        </p>
        {features.length > 0 && (
          <ul className="space-y-2 mt-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span className="text-green-500 mt-1">✓</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </ModernCard>
  );
};

export const StatCard = ({ 
  icon, 
  value, 
  label, 
  trend,
  trendUp = true,
  className = '' 
}) => {
  return (
    <ModernCard variant="gradient" glow className={clsx('text-center', className)}>
      <CardBody className="space-y-3">
        <div className="text-4xl mx-auto w-fit">{icon}</div>
        <div className="text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent">
          {value}
        </div>
        <div className="text-gray-600 dark:text-gray-300 font-medium">
          {label}
        </div>
        {trend && (
          <div className={clsx(
            'text-sm font-semibold',
            trendUp ? 'text-green-600' : 'text-red-600'
          )}>
            {trendUp ? '↑' : '↓'} {trend}
          </div>
        )}
      </CardBody>
    </ModernCard>
  );
};

export const TestimonialCard = ({ 
  name, 
  role, 
  company,
  image,
  quote,
  rating = 5,
  className = '' 
}) => {
  return (
    <ModernCard variant="glass" className={className}>
      <CardBody className="space-y-6">
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => (
            <span 
              key={i} 
              className={clsx(
                'text-xl',
                i < rating ? 'text-yellow-400' : 'text-gray-300'
              )}
            >
              ★
            </span>
          ))}
        </div>
        <p className="text-gray-700 dark:text-gray-300 italic leading-relaxed">
          "{quote}"
        </p>
        <div className="flex items-center gap-4">
          {image && (
            <img 
              src={image} 
              alt={name}
              className="w-12 h-12 rounded-full object-cover ring-2 ring-blue-500"
            />
          )}
          <div>
            <div className="font-bold text-gray-900 dark:text-white">{name}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {role} {company && `at ${company}`}
            </div>
          </div>
        </div>
      </CardBody>
    </ModernCard>
  );
};
